import { apiClient, withErrorHandling, useAPIErrorHandling } from '../utils/apiErrorHandling';

/**
 * Shopify 제품 관리를 위한 훅
 */
export const useShopify = (showSnackbarFn = null, updateProductDataFn = null) => {
    const { handleAPISuccess } = useAPIErrorHandling(showSnackbarFn);

    /**
     * 제품 게시 (Shopify에 제품 생성 및 게시)
     * @param {Object} product - 제품 정보
     * @returns {Promise<Object>} 게시 결과
     */
    const publishProduct = async (product) => {
        if (!product) {
            throw new Error('제품 정보가 없습니다.');
        }

        const publishResult = await withErrorHandling(
            () => apiClient.post(`/shopify/products/${product.id}/publish`),
            {
                onSuccess: (data) => {
                    handleAPISuccess('제품이 성공적으로 게시되었습니다.');
                    console.log('publishResult.data:', data);
                },
                showSnackbar: true,
                showSnackbarFn: showSnackbarFn
            }
        );

        // 게시 성공 시 shopify_product_id 업데이트
        // withErrorHandling은 Axios 응답 객체를 { success, data: axiosResponse } 형태로 반환합니다.
        // 실제 서버 JSON은 axiosResponse.data에 있습니다.
        const publishResponse = publishResult?.data; // AxiosResponse
        const publishPayload = publishResponse?.data || publishResponse; // 서버 JSON 페이로드
        const shopifyProductId = publishPayload?.shopify_product_id;

        if (publishResult.success && shopifyProductId && updateProductDataFn) {
            const updates = {
                shopify_product_id: shopifyProductId.toString(),
                published_to_shopify: true,
                published_at: new Date().toISOString()
            };

            await updateProductDataFn(product, updates, null); // 조용히 업데이트 (사용자에게는 게시 성공 메시지만 표시)
            console.log('Shopify Product ID가 업데이트되었습니다:', shopifyProductId);
        }

        return publishResult;
    };

    /**
     * Shopify 제품 업데이트
     * @param {Object} product - 제품 정보
     * @returns {Promise<Object>} 업데이트 결과
     */
    const updateShopifyProduct = async (product) => {
        if (!product) {
            throw new Error('제품 정보가 없습니다.');
        }

        if (!product.shopify_product_id) {
            throw new Error('Shopify 제품 ID가 없습니다.');
        }

        const updateResult = await withErrorHandling(
            () => apiClient.post(`/shopify/products/${product.id}/update`),
            {
                onSuccess: (data) => {
                    handleAPISuccess('Shopify 제품이 성공적으로 업데이트되었습니다.');
                    console.log('updateResult.data:', data);
                },
                showSnackbar: true,
                showSnackbarFn: showSnackbarFn
            }
        );

        return updateResult;
    };

    /**
     * 제품 상태 변경 (draft <-> active)
     * Shopify와 DB 상태를 모두 변경합니다.
     * @param {Object} product - 제품 정보 (단일 제품)
     * @param {string} newStatus - 새로운 상태 ('draft' 또는 'active')
     * @returns {Promise<Object>} 상태 변경 결과
     */
    const updateProductStatus = async (product, newStatus) => {
        if (!product) {
            throw new Error('제품 정보가 없습니다.');
        }

        if (!product.shopify_product_id) {
            throw new Error('Shopify 제품 ID가 없습니다.');
        }

        if (newStatus !== 'draft' && newStatus !== 'active') {
            throw new Error('올바른 상태를 입력해주세요. (draft 또는 active)');
        }

        const statusResult = await withErrorHandling(
            () => apiClient.post('/shopify/products/bulk-update-status', { 
                product_ids: [product.id],
                status: newStatus
            }),
            {
                onSuccess: (data) => {
                    handleAPISuccess(`제품 상태가 ${newStatus === 'draft' ? '초안' : '활성화'}로 변경되었습니다.`);
                    console.log('statusResult.data:', data);
                },
                showSnackbar: true,
                showSnackbarFn: showSnackbarFn
            }
        );

        // 상태 변경 성공 시 로컬 상태도 업데이트
        if (statusResult.success && updateProductDataFn) {
            const updates = {
                status: newStatus
            };

            await updateProductDataFn(product, updates, null); // 조용히 업데이트 (사용자에게는 상태 변경 성공 메시지만 표시)
        }

        return statusResult;
    };

    /**
     * 여러 제품의 상태를 일괄 변경
     * @param {Array<string>} productIds - 제품 ID 배열
     * @param {string} newStatus - 새로운 상태 ('draft' 또는 'active')
     * @returns {Promise<Object>} 상태 변경 결과
     */
    const bulkUpdateProductStatus = async (productIds, newStatus) => {
        if (!productIds || productIds.length === 0) {
            throw new Error('제품 ID가 없습니다.');
        }

        if (newStatus !== 'draft' && newStatus !== 'active') {
            throw new Error('올바른 상태를 입력해주세요. (draft 또는 active)');
        }

        const statusResult = await withErrorHandling(
            () => apiClient.post('/shopify/products/bulk-update-status', { 
                product_ids: productIds,
                status: newStatus
            }),
            {
                onSuccess: (data) => {
                    handleAPISuccess(`${productIds.length}개 제품의 상태가 ${newStatus === 'draft' ? '초안' : '활성화'}로 변경되었습니다.`);
                    console.log('bulkStatusResult.data:', data);
                },
                showSnackbar: true,
                showSnackbarFn: showSnackbarFn
            }
        );

        return statusResult;
    };

    return {
        publishProduct,
        updateShopifyProduct,
        updateProductStatus,
        bulkUpdateProductStatus
    };
};

